import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Globe2, Target, Zap, BadgeCheck, MapPin, Wallet, Trophy, TrendingUp, Clock, Users, Star } from "lucide-react";
import api, { num, money } from "@/lib/api";

const HERO_IMG = "https://images.unsplash.com/photo-1686984096026-23d6e82f9749?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";
const HERO_VIDEO = "https://assets.mixkit.co/videos/49926/49926-720.mp4";

function Stat({ label, value }) {
  return (
    <div className="text-center">
      <div className="font-mono-metric text-3xl sm:text-4xl font-bold text-[#0A1128]">{value}</div>
      <div className="text-xs uppercase tracking-widest text-slate-500 mt-1">{label}</div>
    </div>
  );
}

export default function Home() {
  const [stats, setStats] = useState(null);
  const [videoOk, setVideoOk] = useState(true);
  useEffect(() => { api.get("/public/stats").then((r) => setStats(r.data)).catch(() => {}); }, []);

  const perks = [
    { icon: Target, title: "Real missions, real work", desc: "Field visits, lead generation, meetings and sales — every task is verified, tracked and counts toward your record." },
    { icon: Wallet, title: "Earnings you can see", desc: "Every completed task updates your earnings dashboard in real time. No guessing, no chasing — full transparency." },
    { icon: BadgeCheck, title: "Badges & streaks", desc: "Unlock achievement badges for tasks, leads and sales streaks. Your consistency becomes visible to the whole network." },
    { icon: Trophy, title: "Rank up the leaderboard", desc: "Compete with workers in your city, country and across the globe. Top performers get noticed first." },
    { icon: TrendingUp, title: "A career, not a gig", desc: "Start as a worker, grow into a team lead, manage a city. The hierarchy is real — and so is the path up." },
    { icon: Clock, title: "Work on your schedule", desc: "Pick missions in your city when it suits you. GTForce runs across time zones, so opportunity never sleeps." },
  ];

  const steps = [
    { n: "01", title: "Create your profile", desc: "Sign up in minutes, tell us your city and skills, and get verified by your local team." },
    { n: "02", title: "Accept missions", desc: "Get assigned tasks, leads and field visits matched to your location and performance tier." },
    { n: "03", title: "Complete, earn, climb", desc: "Submit evidence, track your earnings live, earn badges and rise through the ranks." },
  ];

  const voices = [
    { quote: "I can see exactly what I earned after every task. That transparency changed everything for me.", name: "Field Worker", place: "Lagos" },
    { quote: "The streaks and badges sound small, but they push you. I went from new joiner to team lead in months.", name: "Team Lead", place: "Mumbai" },
    { quote: "I pick up missions between classes. It fits my life instead of the other way around.", name: "Part-time Worker", place: "São Paulo" },
  ];

  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 grid-glow opacity-70" />
        <div className="relative max-w-7xl mx-auto px-5 pt-16 pb-20 grid lg:grid-cols-2 gap-12 items-center">
          <div className="fade-up">
            <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-[#1D4ED8] bg-blue-50 px-3 py-1.5 rounded-full">
              <span className="h-2 w-2 rounded-full bg-[#1D4ED8] animate-pulse" /> Now recruiting field workers worldwide
            </span>
            <h1 className="font-head text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] mt-5 text-[#0A1128]">
              Your city.<br />
              Your missions.<br />
              <span className="text-[#1D4ED8]">Your rise.</span>
            </h1>
            <p className="mt-5 text-lg text-slate-600 max-w-xl">
              Join a global task force where every task you complete is tracked, every earning is visible, and every streak pushes you higher. This isn't a gig app — it's a career engine.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/register" data-testid="hero-join-btn" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#1D4ED8] text-white font-semibold hover:bg-[#1E40AF] transition-colors shadow-lg shadow-blue-600/30">
                Join the Force — Free <ArrowRight size={18} />
              </Link>
              <Link to="/how-it-works" data-testid="hero-how-it-works" className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-slate-200 font-semibold text-[#0A1128] hover:border-[#1D4ED8] hover:text-[#1D4ED8] transition-colors">
                See How It Works
              </Link>
            </div>
            <p className="mt-4 text-xs text-slate-500">Free to join · No experience needed · Missions matched to your city</p>
          </div>
          <div className="relative fade-up">
            <div className="rounded-2xl overflow-hidden border border-slate-200 shadow-2xl">
              {videoOk ? (
                <video src={HERO_VIDEO} poster={HERO_IMG} autoPlay muted loop playsInline preload="auto"
                  onError={() => setVideoOk(false)}
                  className="w-full h-[380px] object-cover" aria-label="Worker completing missions from home" data-testid="hero-video" />
              ) : (
                <img src={HERO_IMG} alt="Worker working from home on a laptop" className="w-full h-[380px] object-cover" data-testid="hero-image" />
              )}
            </div>
            <div className="absolute -bottom-6 -left-4 bg-[#0A1128] text-white rounded-xl p-4 shadow-xl hidden sm:block" data-testid="hero-live-workers-card">
              <div className="text-xs text-slate-400 uppercase tracking-widest">In the force now</div>
              <div className="font-mono-metric text-2xl font-bold">{stats ? num(stats.total_workers) : "—"} <span className="text-sm text-[#D4AF37]">workers</span></div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="border-y border-slate-100 bg-slate-50/60" data-testid="live-stats-band">
        <div className="max-w-6xl mx-auto px-5 py-10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <Stat label="Workers in the Force" value={stats ? num(stats.total_workers) : "—"} />
            <Stat label="Cities Active" value={stats ? num(stats.cities) : "—"} />
            <Stat label="Countries" value={stats ? num(stats.countries) : "—"} />
            <Stat label="Revenue Generated" value={stats ? money(stats.revenue) : "—"} />
          </div>
          <p className="text-center text-[11px] text-slate-400 mt-6 uppercase tracking-widest">Live platform data</p>
        </div>
      </section>

      {/* Why workers join */}
      <section className="max-w-7xl mx-auto px-5 py-20">
        <div className="max-w-2xl">
          <h2 className="font-head text-3xl sm:text-4xl font-bold tracking-tight text-[#0A1128]">Why workers never want to leave.</h2>
          <p className="mt-4 text-slate-600 text-lg">Most platforms treat you as invisible labor. GTForce treats you as an operator on a mission — with proof of work, visible progress and a real path upward.</p>
        </div>
        <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {perks.map((f) => (
            <div key={f.title} className="group p-7 rounded-2xl border border-slate-200 hover:border-[#1D4ED8] hover:shadow-xl transition-all bg-white" data-testid={`perk-${f.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
              <div className="h-12 w-12 rounded-xl bg-[#0A1128] text-[#D4AF37] grid place-items-center group-hover:bg-[#1D4ED8] group-hover:text-white transition-colors">
                <f.icon size={22} />
              </div>
              <h3 className="font-head text-xl font-semibold mt-5 text-[#0A1128]">{f.title}</h3>
              <p className="mt-2 text-slate-600 text-sm leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section className="bg-slate-50/60 border-y border-slate-100">
        <div className="max-w-7xl mx-auto px-5 py-20">
          <div className="max-w-2xl">
            <h2 className="font-head text-3xl sm:text-4xl font-bold tracking-tight text-[#0A1128]">From sign-up to first payout.</h2>
            <p className="mt-4 text-slate-600 text-lg">Three steps. No interviews, no waiting lists, no hidden fees.</p>
          </div>
          <div className="mt-12 grid md:grid-cols-3 gap-6">
            {steps.map((s) => (
              <div key={s.n} className="relative p-7 rounded-2xl bg-white border border-slate-200" data-testid={`step-${s.n}`}>
                <div className="font-mono-metric text-4xl font-bold text-[#D4AF37]">{s.n}</div>
                <h3 className="font-head text-xl font-semibold mt-4 text-[#0A1128]">{s.title}</h3>
                <p className="mt-2 text-slate-600 text-sm leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
          <div className="mt-10">
            <Link to="/register" data-testid="how-it-works-cta" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#0A1128] text-white font-semibold hover:bg-[#1D4ED8] transition-colors">
              Start Step One <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* Voices */}
      <section className="max-w-7xl mx-auto px-5 py-20">
        <div className="max-w-2xl">
          <h2 className="font-head text-3xl sm:text-4xl font-bold tracking-tight text-[#0A1128]">The force speaks.</h2>
          <p className="mt-4 text-slate-600 text-lg">What a day on the force feels like — in workers' own words.</p>
        </div>
        <div className="mt-12 grid md:grid-cols-3 gap-6">
          {voices.map((v) => (
            <figure key={v.place} className="p-7 rounded-2xl border border-slate-200 bg-white" data-testid={`voice-${v.place.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
              <div className="flex gap-1 text-[#D4AF37]">
                {[...Array(5)].map((_, i) => <Star key={i} size={14} fill="#D4AF37" />)}
              </div>
              <blockquote className="mt-4 text-slate-700 leading-relaxed">“{v.quote}”</blockquote>
              <figcaption className="mt-5 text-sm">
                <span className="font-semibold text-[#0A1128]">{v.name}</span>
                <span className="text-slate-500"> · {v.place}</span>
              </figcaption>
            </figure>
          ))}
        </div>
        <p className="mt-6 text-xs text-slate-400 italic">Illustrative worker stories representative of typical platform experiences.</p>
      </section>

      {/* Global band */}
      <section className="bg-[#0A1128] text-white">
        <div className="max-w-7xl mx-auto px-5 py-20 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="font-head text-3xl sm:text-4xl font-bold tracking-tight">A force that never sleeps.</h2>
            <p className="mt-4 text-slate-300 text-lg">As one region winds down, the next comes online. Wherever you are, there's a mission board with your name on it.</p>
            <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 gap-3">
              {(stats?.city_workforce || []).slice(0, 6).map((c) => (
                <div key={c.name} className="rounded-xl bg-white/5 border border-white/10 px-4 py-3">
                  <div className="flex items-center gap-1.5 text-xs text-slate-400"><MapPin size={12} /> {c.name}</div>
                  <div className="font-mono-metric text-xl font-bold mt-1">{num(c.workers)}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="rounded-2xl overflow-hidden border border-white/10">
            <img src="https://images.unsplash.com/photo-1758598497148-bfaea6f82d81?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200" alt="Remote worker on a call with laptop at home" className="w-full h-[340px] object-cover" />
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 grid-glow opacity-50" />
        <div className="relative max-w-5xl mx-auto px-5 py-20 text-center">
          <h2 className="font-head text-3xl sm:text-4xl font-bold text-[#0A1128]">Your first mission is waiting.</h2>
          <p className="mt-4 text-slate-600 text-lg">Every day you wait is a mission someone else completed. Join <span className="font-semibold text-[#1D4ED8]">{stats ? num(stats.total_workers) : ""} workers</span> already on the board.</p>
          <div className="mt-8 flex justify-center gap-3 flex-wrap">
            <Link to="/register" data-testid="cta-register-btn" className="px-7 py-3 rounded-full bg-[#1D4ED8] text-white font-semibold hover:bg-[#1E40AF] transition-colors shadow-lg shadow-blue-600/30 inline-flex items-center gap-2">
              Join the Force — Free <ArrowRight size={18} />
            </Link>
            <Link to="/login" data-testid="cta-login-btn" className="px-7 py-3 rounded-full border border-slate-200 font-semibold text-[#0A1128] hover:border-[#1D4ED8] hover:text-[#1D4ED8] transition-colors">Sign In</Link>
          </div>
          <p className="mt-5 text-xs text-slate-500 inline-flex items-center gap-1.5"><Globe2 size={14} /> Operating across {stats ? num(stats.countries) : "—"} countries · <Zap size={14} /> Missions posted daily · <Users size={14} /> Teams recruiting now</p>
        </div>
      </section>
    </div>
  );
}
